import { useEffect } from 'react';
import SiteNavbar from '../../../components/feature/SiteNavbar';
import SiteTrustBar from '../../../components/feature/SiteTrustBar';
import SiteFooter from '../../../components/feature/SiteFooter';
import PageHero from '../../../components/feature/PageHero';
import PageAiAnswer from '../../../components/feature/PageAiAnswer';
import PageProcess from '../../../components/feature/PageProcess';
import PageWhyChoose from '../../../components/feature/PageWhyChoose';
import PageAreas from '../../../components/feature/PageAreas';
import PageTestimonials from '../../../components/feature/PageTestimonials';
import PageFaq from '../../../components/feature/PageFaq';
import PageBookingCta from '../../../components/feature/PageBookingCta';
import AirportServiceBreakdown from './components/ServiceBreakdown';
import type { ProcessStep } from '../../../components/feature/PageProcess';

const airportProcess: ProcessStep[] = [
  { number: '01', icon: 'ri-calendar-2-line', title: 'Provide Flight Details', description: 'Share your flight number, arrival or departure terminal, and travel date when booking. Takes less than 2 minutes.' },
  { number: '02', icon: 'ri-radar-line', title: 'We Track Your Flight', description: 'Our system monitors your flight in real-time. We automatically adjust your pickup time if there are any delays, early arrivals, or gate changes.' },
  { number: '03', icon: 'ri-car-fill', title: 'Chauffeur Arrives Early', description: 'Your driver arrives 15 minutes ahead of your pickup time, vehicle ready at the curb, with a sign displaying your name for easy identification.' },
  { number: '04', icon: 'ri-home-4-line', title: 'Smooth Door-to-Door Transfer', description: 'Luggage handled, route optimized, and you\'re on your way — relaxed, comfortable, and on time to your destination.' },
];

const airportFaqs = [
  { q: 'Do you track flights for airport transportation in Charleston?', a: 'Yes, Tec\'s Limo Service monitors all flights in real-time and adjusts pickup times to ensure timely service even if your flight is delayed, arrives early, or experiences gate changes. You never need to call us — we\'re already watching your flight.' },
  { q: 'What airports do you serve near Charleston, SC?', a: 'We provide professional airport transportation at Charleston International Airport (CHS), Charleston Executive Airport, and Mt. Pleasant Regional Airport. All locations include the same premium service, flight tracking, and on-time guarantee.' },
  { q: 'How early should I book airport transportation?', a: 'We recommend booking your airport transfer at least 24–48 hours in advance to guarantee availability. However, Tec\'s Limo Service can often accommodate last-minute and same-day airport bookings based on current driver availability.' },
  { q: 'What happens if my flight is delayed?', a: 'If your flight is delayed, we know before you do. Our flight tracking system monitors every departure and arrival in real time, and we automatically adjust your driver\'s arrival time so they\'re waiting when you actually land — not hours before.' },
  { q: 'Is airport transportation available at night in Charleston?', a: 'Yes. Tec\'s Limo Service operates 24 hours a day, 7 days a week, including late-night and early-morning airport transfers. We serve the earliest departures and latest arrivals with the same professional, luxury service every time.' },
  { q: 'Do you provide luggage assistance at the airport?', a: 'Yes. Your professional chauffeur handles all luggage loading and unloading. No heavy lifting required — just walk to the vehicle and let us take care of the rest.' },
  { q: 'How much does airport transportation cost in Charleston?', a: 'Airport transportation pricing depends on pickup location, destination, and vehicle type. Tec\'s Limo Service offers clear, transparent rates with no hidden fees. Call (843) 642-2440 or submit a quote request for accurate pricing.' },
  { q: 'Do you offer meet-and-greet airport service?', a: 'Yes. Your chauffeur will be waiting at the designated pickup area with a name sign, ready to assist with luggage and guide you directly to the vehicle. A smooth, professional greeting every single time.' },
];

const aiFeatures = [
  { icon: 'ri-radar-line', title: 'Real-Time Flight Tracking', desc: 'We monitor your flight 24/7 and automatically adjust pickup times for delays, early arrivals, or gate changes' },
  { icon: 'ri-map-pin-2-fill', title: '3 Airports Covered', desc: 'CHS International, Charleston Executive Airport, and Mt. Pleasant Regional — same premium service at all locations' },
  { icon: 'ri-time-fill', title: 'Always On Time', desc: 'Your driver arrives 15 minutes early — every single time, guaranteed, without exception' },
];

export default function AirportServicePage() {
  useEffect(() => {
    document.title = 'Airport Transportation Charleston SC | TECS Limo Service — CHS Transfers';
  }, []);

  return (
    <main className="min-w-[1024px]">
      <SiteNavbar />
      <PageHero
        badge="CHS · Executive · Mt. Pleasant Airports"
        headline="Airport Transportation in"
        accentLine="Charleston You Can Rely On"
        description="Professional, on-time airport transfers with real-time flight tracking, early arrivals, and door-to-door service — serving all three Charleston area airports, 24/7."
        image="https://public.readdy.ai/ai/img_res/7aad860620d076295c902dc6b0a1c652.jpg"
        primaryCta={{ text: 'Book Airport Transfer', href: '#contact' }}
        secondaryCta={{ text: 'Call (843) 642-2440', href: 'tel:+18436422440' }}
        stats={[
          { value: '3', label: 'Airports Served' },
          { value: '24/7', label: 'Availability' },
          { value: '100%', label: 'On-Time' },
        ]}
      />
      <SiteTrustBar />
      <AirportServiceBreakdown />
      <PageAiAnswer
        label="Airport Transfer Services"
        title="Expert Airport Transportation in Charleston, SC"
        body="Our airport transportation service includes <strong>real-time flight tracking</strong>, professional luggage assistance, and direct door-to-door transfers to and from Charleston International Airport, Charleston Executive Airport, and Mt. Pleasant Regional Airport."
        bodyExtra="Whether you're arriving for business or departing for a vacation, Tec's Limo Service guarantees a professional chauffeur who arrives early, monitors your flight, and delivers a seamless, luxury experience from your door to the terminal — or from baggage claim directly to your destination."
        features={aiFeatures}
      />
      <PageProcess
        headline="Your Airport Transfer"
        accentLine="Made Simple"
        steps={airportProcess}
      />
      <PageWhyChoose />
      <PageAreas
        headline="Airport Coverage"
        accentLine="Across the Region"
        description="Serving all Charleston area airports with pickup and drop-off coverage across the full metro region — from downtown Charleston to suburban neighborhoods to island destinations."
      />
      <PageTestimonials />
      <PageFaq faqs={airportFaqs} headline="Airport Transfer" subheadline="Questions Answered" />
      <PageBookingCta
        headline="Reserve Your Airport"
        accentLine="Transfer Now"
        description="Book your Charleston airport transfer today. Real-time flight tracking, professional chauffeurs, and guaranteed on-time service — 24/7."
      />
      <SiteFooter />
    </main>
  );
}
